import React, { forwardRef, isValidElement } from 'react';
import classNames from 'classnames';
import { renderChildren, findChildByType } from '@fiverr-private/ui_utils';
import { Container } from '@fiverr-private/layout_components';
import { InputGroupProps } from '../types';
import { GROUP_ID, INPUT_ID, PREFIX_ID, SUFFIX_ID } from '../constants';
import { errorClass, inputSearchSelector, successClass } from '../styles.ve.css';
import { filterProps } from '../../shared/utils';
import { getBorderStyling } from '../../shared/getTextInputsStyle';
import { container, borderFocusWithinVar } from './styles.ve.css';

const InputGroup = forwardRef<HTMLInputElement, InputGroupProps>(
  (
    {
      children,
      disabled,
      isSuccess,
      isError,
      dataTestId,
      onBlur,
      dataTrackTag = 'input_group',
      dataTrackValue,
      ...rest
    },
    ref
  ) => {
    const input = findChildByType(children, INPUT_ID);
    const { stylingProps, restProps } = filterProps(rest);
    const { focusBorderColor, borderColor, inlineVars } = getBorderStyling({
      disabled,
      isError,
      isSuccess,
      borderFocusWithinVar,
    });

    if (!isValidElement(input)) {
      return null;
    }

    const prefix = findChildByType(children, PREFIX_ID);
    const suffix = findChildByType(children, SUFFIX_ID);

    const className = classNames(container, inputSearchSelector, {
      [errorClass]: isError,
      [successClass]: isSuccess,
    });

    return (
      <Container
        dataTrackTag={dataTrackTag}
        dataTrackValue={dataTrackValue}
        onBlur={onBlur}
        display="flex"
        direction="row"
        paddingY="2"
        paddingX="3"
        borderRadius="lg"
        alignItems="center"
        backgroundColor={disabled ? 'grey_200' : 'background'}
        width="100%"
        borderColor={borderColor}
        outlineWidth={{
          focusWithin: 'sm',
        }}
        outlineColor={{ focusWithin: focusBorderColor }}
        outlineStyle={{ focusWithin: 'solid' }}
        borderWidth="sm"
        className={className}
        data-testid={dataTestId}
        style={inlineVars}
        {...stylingProps}
      >
        {renderChildren(prefix, { disabled })}
        {renderChildren(input, {
          className: input.props.className,
          disabled,
          isSuccess,
          isError,
          ref,
          ...restProps,
          resetInputStyle: true,
        })}
        {renderChildren(suffix, { disabled })}
      </Container>
    );
  }
);

InputGroup.displayName = GROUP_ID;

export default InputGroup;
